'use client'

import {
  ColorBaseWhite,
  SizeBorder2,
  SizeSpacing2,
  SizeSpacing3,
  SizeSpacing8,
} from '@snowball-tech/design-tokens/dist/web/typescript/design-tokens'
import { Button } from '@snowball-tech/fractal/dist/src/components/Button'
import {
  Paper,
  PaperElevations,
} from '@snowball-tech/fractal/dist/src/components/Paper'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { cj } from '@snowball-tech/fractal/dist/src/styles/helpers'
import { UilFileDownload as DownloadIcon } from '@tooni/iconscout-unicons-react'

import { ForwardedRef, forwardRef } from 'react'

import isEmpty from 'lodash/fp/isEmpty'

import type { ChildrenProps } from '../../types'

import { Apps } from '../../enums'
import { getUrl } from '../../helpers/navigation'
import useApi from '../../hooks/useApi'
import useLogger from '../../hooks/useLogger'
import useTranslations from '../../hooks/useTranslations'
import useUrls from '../../hooks/useUrls'
import { asPx, spacings } from '../../styles/helpers'
import LegacyTable from '../LegacyTable'
import Link from '../Link'

export const TYPE = 'file'

type Props = {
  editionUuid: string
  editionSlug?: string
  empty?: boolean
  filename?: string
  forHtml?: boolean
  id?: string
  readOnly?: boolean
  trackDownload?: boolean
  url?: string
} & Partial<ChildrenProps>
const File = forwardRef<HTMLElement, Props>(
  (
    {
      children,
      editionSlug,
      editionUuid,
      empty = true,
      filename,
      forHtml = false,
      id,
      readOnly = true,
      trackDownload = false,
      url,
    }: Props,
    ref: ForwardedRef<HTMLElement>,
  ) => {
    const $t = useTranslations()

    const endpoints = useApi()

    const logger = useLogger()

    const assetsURLs = useUrls()

    const htmlId = isEmpty(id) ? undefined : `${TYPE}-${id}`

    const hasUrl = !isEmpty(url)

    if (readOnly && !hasUrl) {
      return <div className="hidden" />
    }

    const filenameWithDefault =
      filename ||
      url?.split('/').pop() ||
      $t('general.the_file').toLocaleLowerCase()

    const placeholder =
      empty && !readOnly
        ? $t.notEmpty(`editor.file_caption.placeholder`) ||
          $t('editor.placeholder')
        : undefined

    const downloadUrl = `${getUrl(Apps.Media, `download/${editionSlug || editionUuid}/${id}`)}`

    const trackFileDownload = () => {
      if (isEmpty(id) && isEmpty(url)) {
        return
      }

      // Fire and forget the analytics tracking call.
      endpoints.analytics.track
        .download(
          editionUuid,
          id ?? 'unknown',
          downloadUrl ?? url ?? filenameWithDefault,
          'media',
        )
        .catch((trackingError) => {
          const error = trackingError as Error

          logger.warn(
            `[EDITION] An error occurred when tracking the download of the file "${url}" in block "${id ?? 'unknown'}" for "${editionUuid}": "${error.message}"`,
            { blockId: id, downloadUrl, editionUuid, filenameWithDefault, url },
            error,
          )
        })
    }

    return forHtml ? (
      <LegacyTable
        id={htmlId}
        cellStyle={[
          {
            width: '100%',
          },
          { width: `${spacings[6] + spacings[2]}px` },
        ]}
        center
        className="media-block"
        data-filename={filenameWithDefault}
        data-type={TYPE}
        data-url={url}
        elevation={PaperElevations.Elevated}
        importantWidth={false}
        style={{
          backgroundColor: ColorBaseWhite,
          marginBottom: asPx(SizeSpacing3, { withUnit: true }),
          maxWidth: 'min(100%, 400px)',
          minHeight: asPx(SizeSpacing8, { withUnit: true }),
          width: '100%',
        }}
      >
        <Typography
          ref={empty ? undefined : ref}
          href={downloadUrl}
          inlineStyle
          style={{
            display: 'block',
            overflow: 'hidden',
            padding: asPx(SizeSpacing3, { withUnit: true }),
            paddingLeft: asPx(SizeSpacing2, { withUnit: true }),
            textDecoration: 'none',
            textOverflow: 'ellipsis',
            whiteSpace: 'break-spaces',
            width: '100%',
          }}
          target="_blank"
          variant="body-1-link"
        >
          {empty
            ? $t('edition.file.download', {
                fileName: filenameWithDefault,
              })
            : false}
        </Typography>

        <Typography
          element="a"
          href={downloadUrl}
          inlineStyle
          style={{
            display: 'block',
            padding: `${asPx(SizeSpacing2, { withUnit: true })} 0`,
            paddingRight: asPx(SizeSpacing2, { withUnit: true }),
            textDecoration: 'none',
            width: '100%',
          }}
          target="_blank"
        >
          <Button
            fullStyle
            icon={
              // eslint-disable-next-line @next/next/no-img-element
              <img
                alt=""
                src={`${assetsURLs.assets}/images/icon-file-download.png`}
              />
            }
            iconOnly
            inlineStyle
            label={$t('edition.file.download')}
            style={{
              borderWidth: asPx(SizeBorder2, { withUnit: true }),
            }}
          />
        </Typography>
      </LegacyTable>
    ) : (
      <div className="w-full">
        <div id={htmlId} className="flex flex-col gap-1">
          <Paper
            className="relative flex flex-row size-full justify-center p-2 pointer-events-auto max-h-unset min-h-8 items-center mx-auto min-w-[300px] w-[600px] max-w-[600px] bg-white"
            elevation={PaperElevations.Elevated}
          >
            <div className="flex flex-row gap-3 items-center w-full">
              <Link
                className="flex flex-row gap-3 items-center no-underline flex-1 min-w-0 break-words"
                download={filenameWithDefault}
                href={`${url}?download=${filenameWithDefault}`}
                target="_blank"
                onClick={trackDownload ? trackFileDownload : undefined}
              >
                {readOnly && empty ? (
                  <Typography variant="body-1-bold">
                    {$t('edition.file.download', {
                      fileName: filenameWithDefault,
                    })}
                  </Typography>
                ) : (
                  <Typography
                    ref={ref}
                    aria-placeholder={placeholder}
                    className={cj(
                      'w-full before:w-full',
                      readOnly ? '' : 'cursor-text',
                    )}
                    data-darker-placeholder
                    data-fixed-placeholder
                    variant="body-1-bold"
                  >
                    {ref ? undefined : children}
                  </Typography>
                )}
              </Link>

              <Button
                download={filenameWithDefault}
                href={`${url}?download=${filenameWithDefault}`}
                icon={<DownloadIcon />}
                iconOnly
                label={$t?.('edition.file.download', {
                  fileName: filenameWithDefault,
                })}
                target="_blank"
              />
            </div>
          </Paper>
        </div>
      </div>
    )
  },
)
File.displayName = 'File'

export default File
